import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { Download, Verified, Cancel } from '@mui/icons-material';
import { publicCertificateService } from '../services/api';
import { downloadInternshipCertificate, PrintableCertificate } from '../components/internshipCertificatePdf';
import { fmtDate } from '../components/internshipTypes';

interface PublicCertificate extends PrintableCertificate {
  isValid: boolean;
  isRevoked: boolean;
}

// Public page behind the verification link printed on internship certificates — no login.
// Anyone (an employer, a college, the ex-intern after their account is closed) can confirm the
// certificate is genuine and not revoked, and download a copy while it is valid.
const CertificateVerify: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [certificate, setCertificate] = useState<PublicCertificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!token) return;
    publicCertificateService.verify(token)
      .then(setCertificate)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, maxWidth: 560, width: '100%', alignSelf: 'flex-start', mt: { xs: 2, sm: 6 } }}>
        <Typography variant="h5" gutterBottom>Certificate verification</Typography>
        {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
        {notFound && <Alert severity="error">No certificate matches this link. Please check the URL printed on the certificate.</Alert>}
        {certificate && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 2 }}>
              {certificate.isValid
                ? <><Verified color="success" fontSize="large" /><Typography variant="h6" color="success.main">Valid certificate</Typography></>
                : <><Cancel color="error" fontSize="large" /><Typography variant="h6" color="error.main">This certificate has been revoked</Typography></>}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              <Row label="Issued by" value={certificate.companyName} />
              <Row label="Certificate No" value={certificate.certificateNo} />
              <Row label="Type" value={`Internship ${certificate.certificateType.toLowerCase()}`} />
              <Row label="Name" value={certificate.internName} />
              <Row label="Role" value={`${certificate.roleTitle}${certificate.department ? ` · ${certificate.department}` : ''}`} />
              <Row label="Period" value={`${fmtDate(certificate.startDate)} – ${fmtDate(certificate.endDate)}`} />
              <Row label="Issued on" value={fmtDate(certificate.issuedAt)} />
            </Stack>
            {certificate.isValid && (
              <Button fullWidth variant="contained" startIcon={<Download />} sx={{ mt: 3 }} onClick={() => downloadInternshipCertificate(certificate)}>
                Download certificate
              </Button>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" spacing={2}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value}</Typography>
  </Stack>
);

export default CertificateVerify;
